import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAccount } from "@/lib/account";
import { loginAccount, registerAccount, forgotPassword, startGoogleSignIn } from "@/lib/account-api";
import { formatApiError } from "@/lib/admin-api";

const COPY = {
  signin: ["Welcome back", "Sign in to keep your calendar, logs and settings on every device.", "Sign in"],
  register: ["Create your account", "Save your cycle calendar and pick it up on any device.", "Create account"],
  forgot: ["Reset your password", "Enter your email and we'll send you a link to choose a new password.", "Send reset link"],
};

const Field = ({ id, label, ...props }) => (
  <label className="block text-sm text-[#5C4A42]">
    {label}
    <input id={id} data-testid={`account-${id}-input`} {...props}
      className="mt-1.5 h-11 w-full rounded-xl border border-[#C86D51]/25 bg-[#FDFBF7] px-3 text-[#2C221E] outline-none transition-colors focus:border-[#9E4429]" />
  </label>
);

const GoogleButton = () => (
  <button type="button" onClick={startGoogleSignIn} data-testid="account-google-button"
    className="flex h-11 w-full items-center justify-center gap-2.5 rounded-full border border-[#C86D51]/25 bg-white text-sm font-medium text-[#2C221E] transition-colors hover:border-[#9E4429]">
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
    Continue with Google
  </button>
);

export const AccountDialog = () => {
  const { authMode, openAuth, closeAuth, setUser } = useAccount();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setError(""); }, [authMode]);
  const mode = authMode ?? "signin";
  const [title, sub, cta] = COPY[mode];
  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        await forgotPassword(form.email);
        toast("Check your inbox", { description: "If that email has an account, a reset link is on its way." });
        openAuth("signin");
      } else {
        const u = mode === "register" ? await registerAccount(form) : await loginAccount({ email: form.email, password: form.password });
        setUser(u);
        toast(`Welcome, ${u?.name ?? "friend"}`, { description: "Your calendar now saves to your account." });
        setForm({ email: "", password: "", name: "" });
        closeAuth();
      }
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(authMode)} onOpenChange={(o) => { if (!o) closeAuth(); }}>
      <DialogContent className="max-w-md rounded-3xl border-[#C86D51]/20 bg-[#FBF5EE] p-7" data-testid="account-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-3xl font-light text-[#2C221E]" data-testid="account-dialog-title">{title}</DialogTitle>
          <DialogDescription className="text-[#5C4A42]">{sub}</DialogDescription>
        </DialogHeader>
        {mode !== "forgot" && (
          <>
            <GoogleButton />
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#7A665C]"><span className="h-px flex-1 bg-[#C86D51]/20" />or<span className="h-px flex-1 bg-[#C86D51]/20" /></div>
          </>
        )}
        <form onSubmit={submit} className="space-y-3" data-testid={`account-${mode}-form`}>
          {mode === "register" && <Field id="name" label="First name (optional)" value={form.name} onChange={on("name")} autoComplete="given-name" maxLength={80} />}
          <Field id="email" label="Email" type="email" required value={form.email} onChange={on("email")} autoComplete="email" />
          {mode !== "forgot" && (
            <Field id="password" label={mode === "register" ? "Password (8+ characters)" : "Password"} type="password" required minLength={mode === "register" ? 8 : undefined}
              value={form.password} onChange={on("password")} autoComplete={mode === "register" ? "new-password" : "current-password"} />
          )}
          {error && <p role="alert" className="text-sm text-[#A9503A]" data-testid="account-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="account-submit-button"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#2C221E] text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{cta}
          </button>
        </form>
        <div className="flex flex-wrap justify-between gap-2 text-sm">
          {mode === "signin" && <button type="button" onClick={() => openAuth("forgot")} data-testid="account-forgot-link" className="text-[#5C4A42] underline-offset-2 hover:underline">Forgot password?</button>}
          {mode === "signin"
            ? <button type="button" onClick={() => openAuth("register")} data-testid="account-to-register" className="font-semibold text-[#9E4429]">Create an account</button>
            : <button type="button" onClick={() => openAuth("signin")} data-testid="account-to-signin" className="font-semibold text-[#9E4429]">Back to sign in</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
